/*
	Settings page for managing modules and their configurations
	Includes a grid of module cards with toggle switches
	Fetches module data from the API
*/

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
	Module,
	ModuleCategory,
	ModuleCard,
	toggleModule,
	categoryShort,
	categoryColors,
	getModulesAndCategories,
} from '../features/modules';
import PageHeader from 'layout/PageHeader';
import { faGears } from '@fortawesome/free-solid-svg-icons';

// SettingsModules component
const SettingsModules: React.FC = () => {
	// State management
	const [modules, setModules] = useState<Module[]>([]);
	const [modulesCategories, setModulesCategories] = useState<ModuleCategory[]>(
		[]
	);
	// Retrieve token from localStorage
	const token = localStorage.getItem('token') || '';

	// Hooks de navigation et de localisation
	const { hash } = useLocation();

	// Fetch modules and categories on component mount.
	// Le tri par catégorie et par module est effectué dans getModulesAndCategories (modulesApi.ts).
	useEffect(() => {
		const fetchData = async () => {
			try {
				const { modules, modulesCategories } =
					await getModulesAndCategories(token);
				setModules(modules);
				setModulesCategories(modulesCategories);
			} catch (error) {
				// Handle errors during fetch
				console.error(error);
			}
		};
		// Call the fetch function
		fetchData();
	}, [token]);

	// Scroll when hash changes AND DOM is updated
	useEffect(() => {
		if (!hash) return;

		// delay until DOM update
		setTimeout(() => {
			const id = hash.replace('#', '');
			const el = document.getElementById(id);
			if (el) {
				el.scrollIntoView({ behavior: 'smooth' });
			}
		}, 0);
	}, [hash, modules, modulesCategories]);

	// Handle module toggle
	const handleToggle = async (name: string) => {
		// Find the module by name
		const module = modules.find((m) => m.name === name);
		// If not found, exit
		if (!module) return;
		// Toggle the module's enabled state via API
		try {
			const updated = await toggleModule(token, name, !module.enabled);
			setModules((prev) =>
				prev.map((m) =>
					m.name === name ? { ...m, enabled: updated.enabled } : m
				)
			);
		} catch (error) {
			// Handle errors during toggle
			console.error(error);
		}
	};

	// Render the modules settings page
	return (
		<div>
			{/* Titre de page */}
			<PageHeader
				icon={faGears}
				title="Paramètres"
				subtitle="Gérez les paramètres de l'application"
				className="mb-6 !max-w-full"
			/>
			{modulesCategories.map((category) => (
				<div key={category.id} className="mb-16">
					<div className="flex items-center mb-4">
						{/* Ancre avec le nom de la catégorie */}
						<a id={category.name}>
							<h2 className="text-xl font-medium mr-2">{category.title}</h2>
						</a>
						<span
							className={`px-2 py-0.5 rounded-full text-sm font-medium ${
								categoryColors[category.name] || 'bg-gray-200 text-gray-800'
							}`}
						>
							{categoryShort[category.name] || category.name}
						</span>
					</div>

					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
						{modules
							.filter((m) => m.category === category.name)
							.map((module) => (
								<ModuleCard
									key={module.id}
									module={module}
									onToggle={() => handleToggle(module.name)}
									configurable={module.configurable}
								/>
							))}
					</div>
				</div>
			))}
		</div>
	);
};

// Export the component
export default SettingsModules;
